import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import { apiClient } from "@/lib/api";
import { MotionReveal, MotionStagger, MotionStaggerItem } from "@/components/shared/MotionReveal";
import { CTAButton } from "@/components/shared/CTAButton";
import { useLanguage } from "@/i18n/LanguageContext";

export const InsightsPreview = () => {
  const [items, setItems] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    apiClient.get("/insights").then((res) => setItems(res.data.slice(0, 4))).catch(() => {});
  }, []);

  if (!items.length) return null;
  const [featured, ...rest] = items;

  return (
    <section className="py-16 sm:py-20 lg:py-28 bg-[color:var(--sand)]" data-testid="insights-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <MotionReveal className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("insightsPreview.eyebrow")}</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[color:var(--navy-950)]">
              {t("insightsPreview.heading1")} <span className="italic text-[color:var(--gold-500)]">{t("insightsPreview.headingEmphasis")}</span>
            </h2>
          </div>
          <Link to="/insights" data-testid="view-all-insights-link">
            <CTAButton variant="outlineLight">{t("insightsPreview.viewAll")}</CTAButton>
          </Link>
        </MotionReveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {featured && (
            <MotionReveal>
              <Link
                to={`/insights/${featured.slug}`}
                className="group relative flex flex-col h-full min-h-[320px] justify-end rounded-3xl overflow-hidden bg-[color:var(--navy-950)] p-8 shadow-[0_20px_50px_rgba(11,18,32,0.18)]"
                data-testid="insight-preview-card"
              >
                <div
                  className="absolute inset-0 opacity-30 mix-blend-overlay"
                  style={{ background: "radial-gradient(600px 400px at 20% 0%, rgba(184,154,61,0.6), transparent 60%)" }}
                />
                <span className="relative inline-block w-fit text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full bg-[color:var(--gold-500)] text-[color:var(--navy-950)] mb-4">
                  {t("insightsPreview.featured")} &middot; {featured.category}
                </span>
                <h3 className="relative font-heading text-2xl sm:text-3xl text-white leading-snug group-hover:text-[color:var(--gold-400)] transition-colors">
                  {featured.title}
                </h3>
                <p className="relative mt-3 text-sm text-slate-300 leading-relaxed line-clamp-2">{featured.excerpt}</p>
                <div className="relative mt-5 flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {featured.published_date}</span>
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {featured.read_time_minutes} {t("insightsPreview.minRead")}</span>
                </div>
              </Link>
            </MotionReveal>
          )}

          <MotionStagger className="grid grid-cols-1 gap-4">
            {rest.map((post) => (
              <MotionStaggerItem key={post.slug}>
                <Link
                  to={`/insights/${post.slug}`}
                  className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-[0_14px_40px_rgba(11,18,32,0.08)] hover:border-slate-300 transition-all duration-200"
                  data-testid="insight-preview-card"
                >
                  <div className="shrink-0 h-11 w-11 rounded-full bg-[color:var(--navy-950)]/6 grid place-items-center">
                    <Calendar className="h-4 w-4 text-[color:var(--gold-500)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{post.category}</span>
                    <h3 className="mt-1 font-heading text-base text-[color:var(--navy-950)] leading-snug group-hover:text-[color:var(--gold-500)] transition-colors">
                      {post.title}
                    </h3>
                    <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-slate-500">
                      <Clock className="h-3 w-3" /> {post.read_time_minutes} {t("insightsPreview.minRead")}
                    </span>
                  </div>
                </Link>
              </MotionStaggerItem>
            ))}
          </MotionStagger>
        </div>
      </div>
    </section>
  );
};
